import axiosConfig from "./axiosConfig";
import { PromptRuleChange } from "./getLandlordResearchPromptDiff";
import {
  PromptAddendumModel,
  PromptRuleModel,
} from "./getLandlordResearchPrompt";
import { PromptRuleFields } from "./landlordResearchPromptOps";

// Task-generic client for the unified editable-prompt endpoint (`/leads/ai-prompt*`).
// The backend routes are keyed by a `task` string; the landlord + people-ranking
// clients are the task-bound wrappers that shipped first. This is the single generic
// client the shared PromptRuleSetEditor + any new task (outreach_email) run through,
// so a third near-copy of those 130-line wrapper files is never written.

// One revision of an editable rule-set prompt. Structurally identical to
// LandlordPromptRevision / PeoplePromptRevision (the task-specific clients keep their
// own names for their own call sites); the shared editor types on this generic shape,
// to which those are structurally assignable.
export interface TaskPromptRevision {
  revisionNumber: number;
  // The compiled render (what the model runs on); `addendum` is its source.
  promptText: string;
  addendum: PromptAddendumModel;
  ruleCount: number;
  note: string | null;
  createdBy: string;
  createdAt: string | null;
  isActive: boolean;
  isSeed?: boolean;
}

export interface TaskPromptData {
  // The revision the task currently runs (a saved one, or the seed).
  active: TaskPromptRevision;
  // The shipped built-in prompt text, for a "reset to default" action.
  defaultSeed: string;
  // The shipped built-in prompt as an addendum, for resetting the structured editor.
  defaultSeedAddendum: PromptAddendumModel;
  // The non-editable guardrail appended to whatever prompt is active. Shown read-only.
  enforcedFooter: string;
  // History, newest first, ending with the synthetic seed (revision 1).
  revisions: TaskPromptRevision[];
}

// Every prompt write returns the fresh active revision + history, so the editor
// re-renders from one shape whichever route it called.
export interface TaskPromptWriteResponse {
  active: TaskPromptRevision;
  revisions: TaskPromptRevision[];
}

export interface TaskPromptDiffResponse {
  fromRevision: number;
  toRevision: number;
  preambleChanged: boolean;
  ruleChanges: PromptRuleChange[];
  textDiff: string[];
}

export type { PromptAddendumModel, PromptRuleModel, PromptRuleFields };

const auth = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const getTaskPrompt = async (
  authToken: string,
  task: string
): Promise<TaskPromptData> => {
  const { data } = await axiosConfig.get(`/leads/ai-prompt`, {
    params: { task },
    ...auth(authToken),
  });
  return data;
};

export const getTaskPromptDiff = async (
  authToken: string,
  task: string,
  fromRevision: number,
  toRevision: number
): Promise<TaskPromptDiffResponse> => {
  const { data } = await axiosConfig.get(`/leads/ai-prompt/diff`, {
    params: { task, from: fromRevision, to: toRevision },
    ...auth(authToken),
  });
  return data;
};

// Add (no ruleId) or update one rule.
export const saveTaskPromptRule = async (
  authToken: string,
  task: string,
  body: {
    baseRevision: number;
    ruleId?: string | null;
    rule: PromptRuleFields;
    status?: string;
    note?: string;
  }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/rule`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};

// Delete one rule outright (recoverable by restoring a revision that had it).
export const deleteTaskPromptRule = async (
  authToken: string,
  task: string,
  body: { baseRevision: number; ruleId: string; note?: string }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/rule/delete`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};

// Re-position rules to match `ruleIds` (rules omitted keep their order after).
export const reorderTaskPromptRules = async (
  authToken: string,
  task: string,
  body: { baseRevision: number; ruleIds: string[]; note?: string }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/reorder`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};

// Edit the free-text preamble half of the prompt.
export const saveTaskPromptPreamble = async (
  authToken: string,
  task: string,
  body: { baseRevision: number; preamble: string; note?: string }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/preamble`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};

// Whole-set save: replace the entire addendum in one revision (bulk path).
export const saveTaskPromptAddendum = async (
  authToken: string,
  task: string,
  body: { baseRevision: number; addendum: PromptAddendumModel; note?: string }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};

// Restore an earlier revision by appending a copy of it.
export const restoreTaskPromptRevision = async (
  authToken: string,
  task: string,
  body: { baseRevision: number; revisionNumber: number; note?: string }
): Promise<TaskPromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/restore`,
    { ...body, task },
    auth(authToken)
  );
  return data;
};
